import { html } from 'lit-html';
import { api, get } from '../api.js';
import { act, app, confirmDialog, emptyRow, formValues, helpLink, isAdmin, isOwner, ROLE_LABEL, type Page, type Role } from '../app.js';
import { ago, when } from '../format.js';

// ---------------------------------------------------------------------------
// Exempt agents
// ---------------------------------------------------------------------------
interface Exemption {
  agentId: string;
  agentName: string | null;
  reason: string;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
}
let exemptionList: Exemption[] = [];

export const exemptions: Page = {
  title: 'Exemptions',
  async load() {
    exemptionList = (await get<{ exemptions: Exemption[] }>('/exemptions')).exemptions;
  },
  view() {
    const admin = isAdmin();
    const add = async (e: Event) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const v = formValues(form);
      const ok = await act(
        () => api('PUT', `/exemptions/${encodeURIComponent(v.agentId!.trim())}`, { reason: v.reason, expiresAt: v.until ? new Date(`${v.until}T23:59:59`).toISOString() : null }),
        'Exemption added',
        exemptions.load,
      );
      if (ok) form.reset();
    };
    const remove = async (x: Exemption) => {
      const c = await confirmDialog({ title: `Remove exemption for ${x.agentName ?? x.agentId}?`, body: 'Their future timeouts will be enforced like everyone else\'s.', confirmLabel: 'Remove', tone: 'danger' });
      if (c.ok) await act(() => api('DELETE', `/exemptions/${encodeURIComponent(x.agentId)}`), 'Exemption removed', exemptions.load);
    };
    return html`
      <h1>Exempt agents ${helpLink('exemptions')}</h1>
      <p class="hint">Exempt agents are never deactivated. Their timeouts are still recorded and reported.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Agent</th><th>Reason</th><th>Until</th><th>Added</th>${admin ? html`<th></th>` : ''}</tr></thead>
        <tbody>${exemptionList.length
          ? exemptionList.map((x) => html`<tr>
              <td><b>${x.agentName ?? '(name unavailable)'}</b><br><code class="id">${x.agentId}</code></td>
              <td>${x.reason}</td>
              <td>${x.expiresAt ? (new Date(x.expiresAt) < new Date() ? html`<span class="pill neutral">expired</span>` : when(x.expiresAt)) : 'No end date'}</td>
              <td>${when(x.createdAt)}<br><span class="muted">${x.createdBy}</span></td>
              ${admin ? html`<td><button class="btn destructive outline compact" @click=${() => remove(x)}>Remove</button></td>` : ''}</tr>`)
          : emptyRow(admin ? 5 : 4, 'No exempt agents.')}</tbody>
      </table></div>
      ${admin ? html`<section class="panel"><h2>Add an exemption</h2>
        <form class="inline" @submit=${add}>
          <input name="agentId" placeholder="Genesys user ID" required pattern="[0-9a-fA-F-]{36}" title="The agent's Genesys user ID (a UUID)">
          <input name="reason" placeholder="Reason, e.g. supervisor" required maxlength="500">
          <label class="muted">until <input name="until" type="date"></label>
          <button class="btn">Add</button>
        </form>
        <p class="hint">Find a user ID in Genesys under Admin › People › (person) — it's the last part of the page address.</p>
      </section>` : ''}`;
  },
};

// ---------------------------------------------------------------------------
// Web UI users (admins only)
// ---------------------------------------------------------------------------
interface User {
  email: string;
  role: Role;
  createdBy: string;
  createdAt: string;
  lastSeenAt: string | null;
}
let userList: User[] = [];

/** Roles the signed-in person may hand out: only owners grant owner. */
const grantable = (): Role[] => (isOwner() ? ['viewer', 'admin', 'owner'] : ['viewer', 'admin']);
const addedBy = (actor: string) => (actor === 'system:bootstrap' ? 'deployment config' : actor.replace(/^sso:/, ''));

export const users: Page = {
  title: 'Users',
  adminOnly: true,
  async load() {
    userList = (await get<{ users: User[] }>('/users')).users;
  },
  view() {
    const confirmOwner = async (email: string) =>
      (await confirmDialog({
        title: `Make ${email} an owner?`,
        body: 'Owners are super admins: they can do everything admins can, and also add, change or remove other owners.',
        confirmLabel: 'Make owner',
      })).ok;
    const add = async (e: Event) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const v = formValues(form);
      const email = v.email!.trim();
      if (v.role === 'owner' && !(await confirmOwner(email))) return;
      const ok = await act(() => api('PUT', `/users/${encodeURIComponent(email)}`, { role: v.role }), `${email} added as ${ROLE_LABEL[v.role as Role].toLowerCase()}`, users.load);
      if (ok) form.reset();
    };
    const setRole = async (u: User, role: Role) => {
      if (role === 'owner' && !(await confirmOwner(u.email))) return app.rerender();
      await act(() => api('PUT', `/users/${encodeURIComponent(u.email)}`, { role }), `${u.email} is now ${ROLE_LABEL[role].toLowerCase()}`, users.load);
    };
    const remove = async (u: User) => {
      const c = await confirmDialog({ title: `Remove ${u.email}?`, body: 'They will no longer be able to open this app.', confirmLabel: 'Remove', tone: 'danger' });
      if (c.ok) await act(() => api('DELETE', `/users/${encodeURIComponent(u.email)}`), 'User removed', users.load);
    };
    const me = app.me.actor.replace(/^sso:/, '');
    // Owners' rows are locked for admins; nobody changes their own row.
    const locked = (u: User) => u.email === me || (u.role === 'owner' && !isOwner());
    return html`
      <h1>Users ${helpLink('users')}</h1>
      <p class="hint">People sign in with company SSO. This list decides what they can do: <b>viewers</b> see everything,
        <b>admins</b> can also change settings and add people, and <b>owners</b> (super admins) can also manage other owners.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Email</th><th>Role</th><th>Last seen</th><th>Added by</th><th></th></tr></thead>
        <tbody>${userList.length
          ? userList.map((u) => html`<tr>
              <td>${u.email}${u.email === me ? html` <span class="pill neutral">you</span>` : ''}</td>
              <td>${locked(u)
                ? html`<span class="pill ${u.role === 'owner' ? 'accent' : 'neutral'}">${ROLE_LABEL[u.role]}</span>`
                : html`<select class="compact" aria-label="Role for ${u.email}" @change=${(e: Event) => setRole(u, (e.target as HTMLSelectElement).value as Role)}>
                    ${grantable().map((r) => html`<option value=${r} ?selected=${u.role === r}>${ROLE_LABEL[r]}</option>`)}</select>`}</td>
              <td>${ago(u.lastSeenAt)}</td><td class="muted">${addedBy(u.createdBy)}</td>
              <td>${locked(u) ? '' : html`<button class="btn destructive outline compact" @click=${() => remove(u)}>Remove</button>`}</td></tr>`)
          : emptyRow(5, 'No users yet.')}</tbody>
      </table></div>
      <section class="panel"><h2>Add a person</h2>
        <form class="inline" @submit=${add}>
          <input name="email" type="email" placeholder="name@company.com" required>
          <select name="role" aria-label="Role">${grantable().map((r) => html`<option value=${r}>${ROLE_LABEL[r]}</option>`)}</select>
          <button class="btn">Add</button>
        </form>
        <p class="hint">Use the email they sign in to Okta with. They also need to be assigned to the ACW Enforcer app in Okta.</p>
      </section>`;
  },
};
